import { AdUnitEnv } from '@pubstack/common/src/pbs/adunit'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { AD_UNITS_QUERY_KEYS, validateAdUnitNames } from '~/api/pbs/ad-units.api'

/**
 * Resolves which of the given names are already taken in `env`, in a single request for the whole batch.
 * A name is taken per env, so the env belongs in the key as much as in the request — otherwise the
 * cache would answer an in-app form with what it learned about s2s.
 *
 * Keyed on the names themselves, so React Query provides the cache, the deduplication, the abort
 * signal and the pending state — and re-renders when the answer lands. Keeping the previous data
 * while a new batch resolves avoids errors blinking off on every keystroke.
 *
 * `isTaken` is synchronous: a react-hook-form `validate` rule cannot suspend.
 */
export const useAdUnitNameAvailability = (names: string[], env: AdUnitEnv) => {
  const { data: takenNames, isFetching } = useQuery({
    queryKey: [...AD_UNITS_QUERY_KEYS, 'validate-names', env, [...names].sort()],
    queryFn: ({ signal }) => validateAdUnitNames(names, env, signal),
    enabled: names.length > 0,
    placeholderData: keepPreviousData,
  })

  const taken = new Set(takenNames)

  return { isTaken: (name: string) => taken.has(name), isChecking: isFetching, takenNames }
}
